'use client';

import React, { FC, useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'components/AppSafeLink/AppSafeLink';
import Image from 'next/image';
import '/public/css/components/newsLetter.css';
import { useFeatureFlags, FEATURE_FLAGS } from 'context/FeatureFlagsContext';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

interface Props {
  isSideBar?: boolean;
  landing?: boolean;
  showZipCode?: boolean;
}

const NewsLetter: FC<Props> = ({ isSideBar, landing, showZipCode }) => {
  const router = useRouter();
  const { isFeatureEnabled } = useFeatureFlags();
  const [email, setEmail] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [hasError, setHasError] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setHasError(false);
    setMessage('');

    // Capture UTM parameters from URL query
    const { query } = router;
    const utmParams = {
      utm_source: (query.utm_source as string) || '',
      utm_medium: (query.utm_medium as string) || '',
      utm_campaign: (query.utm_campaign as string) || '',
      utm_term: (query.utm_term as string) || '',
      utm_content: (query.utm_content as string) || '',
    };

    // Get current URL for tracking
    const currentUrl =
      typeof window !== 'undefined' ? window.location.href : '';

    try {
      const response = await fetch('/api/newsletters/updatesubscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          postalcode: postalCode,
          ListIds: ['529'], // Default newsletter ID
          params: {
            ...utmParams,
            url: currentUrl,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to subscribe');
      }

      // GTM tracking
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'newsletter_signup',
          newsletter_id: '529',
          ...utmParams,
        });
      }

      if (
        isFeatureEnabled(FEATURE_FLAGS.DONATION_SIDEBAR_REDIRECT) &&
        isSideBar &&
        typeof window !== 'undefined'
      ) {
        window.open(
          'https://support.mpr.org/secure/mprnews-cfw09-02-newsletter-form',
          '_blank'
        );
      }

      setMessage('Successfully subscribed to newsletter');
      setEmail('');
      setPostalCode('');
      setIsSubmitted(true);
    } catch (error) {
      setHasError(true);
      setMessage('Failed to subscribe. Please try again.');
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`newsletter ${isSideBar ? 'newsletter-sidebar' : ''}`}
      data-testid={isSideBar && 'newsletter-sidebar'}
    >
      {isSideBar && (
        <div className="newsletter-img-container">
          <Image
            src="/img/newsletter.png"
            alt="MPR News daily newsletter"
            height={184}
            width={380}
          />
          <div className="newsletter-icon">
            <img
              src="/img/icon-send.svg"
              alt=""
              /* decorative */
              data-testid="icon-newsletter"
            />
          </div>
        </div>
      )}

      <div className="newsletter-inner">
        <div className="newsletter-heading">
          <div className="newsletter-heading-desktop">
            {!isSideBar && (
              <div className="newsletter-icon">
                <img
                  src="/img/icon-send.svg"
                  alt=""
                  /* decorative */
                  data-testid="icon-newsletter"
                />
              </div>
            )}
          </div>
          <div className="newsletter-heading-mobile">
            {isSideBar && (
              <div className="newsletter-icon">
                <img
                  src="/img/icon-send.svg"
                  alt=""
                  /* decorative */
                  data-testid="icon-newsletter"
                />
              </div>
            )}
          </div>
          <div className="newsletter-heading-text">
            <h2 className={landing ? '' : 'hdg-4'}>
              News you can use in your inbox
            </h2>
          </div>
        </div>
        {(isSideBar || landing) && !isSubmitted && (
          <p data-testid="paragraph">
            When it comes to staying informed in Minnesota, our newsletters
            overdeliver. Sign-up now for headlines, breaking news, hometown
            stories, weather and much more. Delivered weekday mornings.
          </p>
        )}

        {isSubmitted ? (
          <div className={`message ${hasError ? 'error' : 'success'}`}>
            {message}
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="newsletter-form">
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  aria-label="Email address"
                  autoComplete="email"
                  maxLength={254}
                  className="form-control"
                  required
                  disabled={isSubmitting}
                />
              </div>
              {showZipCode && (
                <div>
                  <input
                    type="text"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder="ZIP (optional)"
                    aria-label="ZIP code (optional)"
                    autoComplete="postal-code"
                    inputMode="numeric"
                    maxLength={5}
                    className="form-control"
                    disabled={isSubmitting}
                    pattern="[0-9]{5}"
                    title="Enter a 5-digit ZIP code"
                  />
                </div>
              )}
              <button
                type="submit"
                className="btn btn-primary btn-full-width"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
              {landing && (
                <div className="newsletter-link">
                  <Link
                    className="link basic-link"
                    href="https://www.americanpublicmedia.org/terms"
                    data-testid="termslink"
                    target="_blank"
                  >
                    Terms
                  </Link>
                </div>
              )}
            </form>
            {isSideBar && (
              <div className="newsletter-link">
                <Link
                  className="link"
                  href="https://www.americanpublicmedia.org/terms"
                  data-testid="termslink"
                  target="_blank"
                >
                  Terms
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NewsLetter;
